/**
 * Was eine Rechnungszeile im Vorgang SAGT — und welche Knoepfe sie tragen darf.
 *
 * Anlass (09.08.2026, Befund aus PR #517): `reserve_auto_invoice`/`reserve_auto_storno`
 * (`packages/db/sql/95_invoice_idempotency.sql`, `103_auto_storno_reservation.sql`) und die
 * manuelle Erstellungsroute legen VOR den LexOffice-Aufrufen eine Zeile mit `status='draft'` an.
 * Sie belegt den Eindeutigkeits-Platz, damit ein paralleler Ausloeser keinen zweiten echten Beleg
 * bucht. Der Mechanismus ist richtig; falsch war nur, was die Oberflaeche daraus machte: eine
 * Zeile mit der Beschriftung „Entwurf", ohne Nummer, ohne Betrag — und mit den Knoepfen
 * „An Kunden senden" und „Stornieren" daneben, die auf einem Entwurf serverseitig zwingend
 * scheitern (`not_finalized` 422 bzw. `already_voided` 409).
 *
 * Der Platzhalter wird bewusst WEITER ausgeliefert (Begruendung an der Route,
 * `apps/api/src/routes/invoices.ts`, GET `/api/events/:id/invoices`): der Betrieb soll sehen,
 * dass etwas laeuft, statt dass eine Rechnung aus dem Nichts erscheint. Er muss nur ehrlich
 * beschriftet sein — und dabei die zwei sehr verschiedenen Entwuerfe auseinanderhalten:
 *
 *   draft OHNE Provider-Beleg  -> reine Reservierung, vergeht von selbst (Finalisierung,
 *                                 Freigabe oder Janitor nach 15 Minuten). Nichts zu tun.
 *   draft MIT Provider-Beleg   -> der reale, nummerierte LexOffice-Beleg existiert bereits, nur
 *                                 die Finalisierung kam nicht durch (H-36-Recovery-Zeile). Der
 *                                 Janitor raeumt sie bewusst NICHT weg. Das ist kein
 *                                 Zwischenstand, sondern eine Aufgabe fuer einen Menschen.
 *
 * Rein, ohne React: dieselbe Regel entscheidet Beschriftung UND Knopf-Sichtbarkeit, und beides
 * darf nicht auseinanderlaufen (ein Knopf an einer Zeile, die ihn nie bedienen kann, ist eine
 * Luege in Knopfform).
 */

/** Die Status-Werte, die `invoices.status` annehmen kann (siehe `invoices_status_check`). */
export type RechnungsStatus = "draft" | "open" | "paid" | "voiding" | "voided" | "failed";

export interface RechnungsZeile {
  status: RechnungsStatus | string;
  /** Abgeleitet aus `lex_invoice_id != null` (Feld `hasLexDocument` der Liste). */
  hasLexDocument?: boolean;
  /**
   * sql/332 (Idempotency-Nachtrag PR #793, 30.08.2026): abgeleitet aus
   * `lex_creation_uncertain_at != null`. Die Antwort auf createInvoice/createCreditNote ging
   * verloren (Zeitlimit/Netzfehler/5xx) - LexOffice koennte den Beleg TROTZDEM angelegt haben.
   * Die Reservierung bleibt in diesem Fall bewusst geklaimt statt automatisch freigegeben zu
   * werden (siehe lib/lexoffice-invoice.ts, LexofficeUngewissError) - ohne dieses Feld saehe die
   * Oberflaeche eine gewoehnliche, schnell vergehende `draft`-Zeile ("Wird erstellt …"), obwohl
   * sie tatsaechlich haengt und eine manuelle Pruefung bei LexOffice braucht.
   */
  creationUncertain?: boolean;
  invoiceType?: string;
  sentAt?: string | null;
  /** Abrechnung Stufe 3: Bruttobetrag — negativ bei einer Schlussrechnung mit Minusbetrag. */
  amountGrossCents?: number | null;
}

/**
 * Schlussrechnung MIT Minusbetrag (Anzahlungen > Abrechnung, Antoine 28.09.2026): in LexOffice eine
 * Rechnungskorrektur. Sie ist KEINE offene Forderung — über den Betrag zu Gunsten des Gastes
 * entscheidet die Abrechnung (Rückerstattung oder Guthaben). Übersichten zählen sie nie als offen.
 */
export const istMinusSchlussrechnung = (z: Pick<RechnungsZeile, "invoiceType" | "amountGrossCents">): boolean => z.invoiceType !== "credit" && (z.amountGrossCents ?? 0) < 0;

/** Farb-Charakter der Zustands-Pille — die Seite bildet ihn auf ihre Klassen ab. */
export type ZustandsTon = "neutral" | "warn" | "success" | "danger";

export interface RechnungsZustand {
  label: string;
  ton: ZustandsTon;
  /** Darf die Zeile „An Kunden senden" anbieten? (Route: nur 'open'/'paid'.) */
  sendbar: boolean;
  /** Darf die Zeile „Stornieren" anbieten? (Route: nur 'open'/'paid', keine Gutschrift.) */
  stornierbar: boolean;
  /**
   * Erklaerender Halbsatz, wenn die Zeile ohne ihn missverstanden wird. `null`, wo der Zustand
   * fuer sich spricht — ein Hinweis an jeder Zeile waere Rauschen und niemand laese den einen,
   * auf den es ankommt.
   */
  hinweis: string | null;
  /**
   * Fall Ribeiro (24.09.2026): nur bei „Beleg-Status ungeklärt" — darf die Zeile „In LexOffice
   * nicht vorhanden – freigeben" anbieten? (Route `POST /api/invoices/:id/ungewiss-freigeben`.)
   */
  freigebbar?: boolean;
}

export function rechnungsZustand(zeile: RechnungsZeile): RechnungsZustand {
  const istGutschrift = zeile.invoiceType === "credit";
  switch (zeile.status) {
    case "draft":
      if (zeile.hasLexDocument === true) {
        return {
          label: "Erstellung unterbrochen",
          ton: "danger",
          sendbar: false,
          stornierbar: false,
          hinweis:
            "Der Beleg wurde bei LexOffice bereits angelegt, hier aber nicht fertig gespeichert. Bitte in LexOffice nachsehen.",
        };
      }
      if (zeile.creationUncertain === true) {
        return {
          label: "Beleg-Status ungeklärt",
          ton: "danger",
          sendbar: false,
          stornierbar: false,
          freigebbar: true,
          hinweis:
            "Die Antwort von LexOffice ging verloren - unklar, ob der Beleg dort trotzdem entstanden ist. Bitte in LexOffice nachsehen (kein automatischer erneuter Versuch, um keinen doppelten Beleg zu riskieren). Gibt es dort keinen Beleg, diese Zeile freigeben und die Rechnung neu erstellen.",
        };
      }
      return {
        label: "Wird erstellt …",
        ton: "neutral",
        sendbar: false,
        stornierbar: false,
        hinweis: "Nummer und Betrag kommen von LexOffice, sobald der Beleg dort steht.",
      };
    case "open":
      if (istMinusSchlussrechnung(zeile)) {
        return { label: "Zu Gunsten des Gastes", ton: "neutral", sendbar: true, stornierbar: false, hinweis: "Schlussrechnung mit Minusbetrag – keine offene Forderung. Rückerstattung oder Guthaben entscheidet die Abrechnung." };
      }
      return { label: "Erstellt – offen", ton: "warn", sendbar: true, stornierbar: !istGutschrift, hinweis: null };
    case "paid":
      return { label: "Bezahlt", ton: "success", sendbar: true, stornierbar: !istGutschrift, hinweis: null };
    case "voiding":
      return { label: "Wird storniert …", ton: "warn", sendbar: false, stornierbar: false, hinweis: null };
    case "voided":
      return {
        label: "Storniert",
        ton: "neutral",
        sendbar: false,
        stornierbar: false,
        hinweis: "Fuer stornierte Belege geht die Gutschrift an den Kunden, nicht die Rechnung.",
      };
    case "failed":
      return { label: "Fehlgeschlagen", ton: "danger", sendbar: false, stornierbar: false, hinweis: null };
    default:
      return { label: String(zeile.status), ton: "neutral", sendbar: false, stornierbar: false, hinweis: null };
  }
}
